import type { Step } from "nextstepjs";
import { useMemo } from "react";

import { useJobSalaries } from "@/components/watchlist/hooks/useJobSalaries";
import { TourSelectors } from "@/config/tour.config";
import { DemoContext } from "@/providers/demo/DemoContext";
import { NumberUtils } from "@/util/number.utils";
import { StringUtils } from "@/util/string.utils";

/**
 * Hook to generate dynamic tour content based on demo data
 * Falls back to static content if data is unavailable
 */
export const useDynamicTourContent = (step: Step | undefined) => {
  const { isDemoMode, demoProfile, demoWorthData } = DemoContext.useDemo();
  const { jobSalaries, selectedMetroArea, selectedQuantile } = useJobSalaries({
    defaultToAll: true,
    autoSelectDefaults: true,
  });

  return useMemo(() => {
    if (!step?.content || !isDemoMode) return step?.content;

    // Handle YourNumbersSection step - inject real demo data
    if (step.selector === `#${TourSelectors.YourNumbersSection}`) {
      if (!demoProfile || !demoWorthData) return step.content;

      const firstName = demoProfile.user.name?.split(" ")[0] || "User";
      const jobPosition = demoProfile.jobPosition || "Financial Analyst";
      const city = demoProfile.dataV02?.person?.profile?.location?.city || "Chicago";
      const currentWorth = NumberUtils.formatAsUSCurrency(demoWorthData.current || 0);
      const targetWorth = NumberUtils.formatAsUSCurrency(demoWorthData.implied || 0);
      const isAboveAverage = (demoWorthData.current || 0) > (demoWorthData.historical || 0);

      return `Here it gets exciting. This section lets you compare your value to your industry's average, For example, here ${firstName} is worth ${currentWorth} as a ${jobPosition} in ${city}, which is ${isAboveAverage ? "above" : "below"} average in their field, but following our guidance has the potential to reach ${targetWorth}.`;
    }

    // Handle WatchlistTableRow step - inject job salary data from mock
    if (step.selector === `#${TourSelectors.WatchlistTableRow}`) {
      if (!jobSalaries || jobSalaries.length === 0 || !selectedMetroArea || !selectedQuantile) {
        return step.content;
      }

      // Sort by title (alphabetically) to match table sorting
      const sortedJobs = [...jobSalaries].sort((a, b) => a.title.localeCompare(b.title));

      // Get the appropriate row based on device type
      // Use 1st row (index 0) on mobile, index 11 on desktop
      const rowIndex = 2;
      const highlightedJob = sortedJobs[rowIndex];

      if (!highlightedJob) return step.content;

      const jobTitle = highlightedJob.title;
      const salary = highlightedJob.salary.value || 0;
      // Round to nearest thousand and format with ~ prefix
      const roundedSalary = Math.round(salary / 1000) * 1000;
      const formattedSalary = NumberUtils.formatAsUSCurrency(roundedSalary, { maximumDecimalPlaces: 0 });
      const experienceLevel = selectedQuantile.title || "5-10y exp";
      const metroAreaName = selectedMetroArea.title?.split(",")[0] || "Chicago";

      return `For example, ${StringUtils.getArticle(jobTitle)} ${jobTitle} with ${experienceLevel} should make around ${formattedSalary} in ${metroAreaName}.`;
    }

    return step.content;
  }, [step, isDemoMode, demoProfile, demoWorthData, jobSalaries, selectedMetroArea, selectedQuantile]);
};
